import {
  LanguageSelect,
  type TLanguageSelectVariant,
} from '@components/common/LanguageSelect';
import { useLanguage } from '@hooks/flow/language/useLangauge';
import { createContext, type ReactNode, useContext, useState } from 'react';

type LocalizedFormLanguageValue = {
  languages: string[];
  // The company's default bot language. Empty translations fall back to it at
  // runtime, so the form treats it as the one that must be filled in.
  defaultLanguage: string;
  currentLanguage: string;
  setCurrentLanguage: (language: string) => void;
  // False for a company with a single language: there is nothing to switch
  // between, so neither the switcher nor the per-field badges earn their space.
  isMultiLanguage: boolean;
};

const LocalizedFormLanguageContext =
  createContext<LocalizedFormLanguageValue | null>(null);

// * Returns null outside a provider, which is what a form with no localized
// * fields sees.
export const useLocalizedFormLanguage = (): LocalizedFormLanguageValue | null =>
  useContext(LocalizedFormLanguageContext);

// * One language selection per form. The localized inputs can sit several
// * levels down - a menu entry is three components deep in the webclient config
// * modal - so this travels by context rather than by props, the same way the
// * form state itself does.
export const LocalizedFormLanguageProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { language, languages } = useLanguage();
  const [pickedLanguage, setPickedLanguage] = useState('');

  return (
    <LocalizedFormLanguageContext.Provider
      value={{
        languages,
        defaultLanguage: language,
        // Empty until the user picks one, so the form follows the company
        // default even if the languages arrive after it mounted.
        currentLanguage: pickedLanguage || language,
        setCurrentLanguage: setPickedLanguage,
        isMultiLanguage: languages.length > 1,
      }}>
      {children}
    </LocalizedFormLanguageContext.Provider>
  );
};

// * The switcher for the surrounding provider. Renders nothing for a
// * single-language company.
export const LocalizedFormLanguageSelect: React.FC<{
  variant?: TLanguageSelectVariant;
}> = ({ variant = 'compact' }) => {
  const formLanguage = useLocalizedFormLanguage();

  if (!formLanguage?.isMultiLanguage) {
    return null;
  }

  const select = (
    <LanguageSelect
      languages={formLanguage.languages}
      currentLanguage={formLanguage.currentLanguage}
      onChange={formLanguage.setCurrentLanguage}
      variant={variant}
    />
  );

  if (variant === 'bar') {
    return select;
  }

  return <div className='flex justify-end mb-2'>{select}</div>;
};
